/**
 * Etecplus custom PC builder: <etec-pc-builder>.
 * Loaded with `defer` by sections/etec-pc-builder.liquid. No dependencies.
 *
 * Products come from /collections/<handle>/products.json (prices there are decimal strings in
 * dollars and are converted to integer cents on load). The tag scheme used by the compatibility
 * check is documented at the top of the section file.
 */
(() => {
  if (customElements.get('etec-pc-builder')) return;

  const STORAGE_KEY = 'etec:pc-build';
  const PAGE_SIZE = 250;
  const MAX_PAGES = 20;
  const RENDER_CHUNK = 40;
  const PASSING_MESSAGE_MS = 6000;
  const MAX_QUANTITY = 99;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

  // Shared helpers from assets/etec-parts.js (loaded first by the section).
  const {
    parseTags,
    checkProduct,
    evaluateBuild,
    el,
    fill,
    moneyText,
    sizedImage,
    rootUrl,
    normaliseProduct,
  } = window.EtecParts;

  const newBuildId = () => {
    const stamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `PC-${stamp}-${random}`;
  };

  const parseBuild = (text) =>
    String(text || '')
      .split(',')
      .map((chunk) => chunk.trim().match(/^(\d+):(\d+)(?:x(\d+))?$/))
      .filter(Boolean)
      .map((match) => ({
        step: parseInt(match[1], 10),
        variantId: Number(match[2]),
        qty: Math.max(1, Math.min(MAX_QUANTITY, parseInt(match[3] || '1', 10))),
      }));

  /* ---------- <etec-pc-builder> ---------------------------------------------- */

  class EtecPcBuilder extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      if (this.ready) return;
      this.ready = true;

      this.strings = {};
      const stringsNode = this.querySelector('[data-pcb-strings]');
      if (stringsNode) {
        try {
          this.strings = JSON.parse(stringsNode.textContent);
        } catch (error) {
          this.strings = {};
        }
      }

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.compatibility = this.dataset.compatibility === 'true';
      this.buildId = newBuildId();
      this.busy = false;

      this.steps = Array.from(this.querySelectorAll('[data-pcb-step]')).map((node) => ({
        el: node,
        index: parseInt(node.dataset.index, 10),
        handle: node.dataset.handle,
        role: node.dataset.role || 'none',
        label: node.dataset.label || '',
        required: node.hasAttribute('data-required'),
        multiple: node.hasAttribute('data-multiple'),
        toggle: node.querySelector('[data-pcb-action="toggle"]'),
        panel: node.querySelector('[data-pcb-panel]'),
        list: node.querySelector('[data-pcb-list]'),
        choice: node.querySelector('[data-pcb-choice]'),
        price: node.querySelector('[data-pcb-step-price]'),
        count: node.querySelector('[data-pcb-count]'),
        more: node.querySelector('[data-pcb-action="more"]'),
        incompatWrap: node.querySelector('[data-pcb-incompat-wrap]'),
        incompatLabel: node.querySelector('[data-pcb-incompat-label]'),
        products: null,
        loading: null,
        error: false,
        open: false,
        query: '',
        sort: 'featured',
        showIncompatible: false,
        shown: RENDER_CHUNK,
        variantChoice: {},
        selections: [],
      }));

      this.summary = this.querySelector('[data-pcb-summary]');
      this.summaryBox = this.querySelector('[data-pcb-summary-box]');
      this.compatBox = this.querySelector('[data-pcb-compat]');
      this.message = this.querySelector('[data-pcb-message]');
      this.barMessage = this.querySelector('[data-pcb-bar-message]');
      this.shareField = this.querySelector('[data-pcb-share-field]');
      this.announcer = this.querySelector('[data-pcb-announce]');
      this.quote = this.querySelector('[data-pcb-quote]');

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));

      // Theme editor: open the step whose block is selected.
      this.addEventListener('shopify:block:select', (event) => {
        const step = this.steps.find((item) => item.el === event.target);
        if (step) this.setOpen(step, true);
      });

      this.refresh();
      this.restore();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      window.clearTimeout(this.messageTimer);
    }

    t(key, values) {
      return fill(this.strings[key] || '', values || {});
    }

    money(cents) {
      return moneyText(cents, this.moneyFormat);
    }

    /* ----- State ----- */

    stepByIndex(index) {
      return this.steps.find((step) => step.index === Number(index));
    }

    selectionFor(step, productId) {
      return step.selections.find((selection) => String(selection.product.id) === String(productId));
    }

    /** Chosen parts as plain objects for the compatibility functions. */
    parts(exceptStep) {
      const parts = [];
      this.steps.forEach((step) => {
        if (step === exceptStep) return;
        step.selections.forEach((selection) => {
          parts.push({
            role: step.role,
            title: selection.product.title,
            data: selection.product.data,
            qty: selection.qty,
          });
        });
      });
      return parts;
    }

    compatFor(step, product) {
      if (!this.compatibility) return null;
      return checkProduct({ role: step.role, data: product.data }, this.parts(step));
    }

    totals() {
      let total = 0;
      let count = 0;
      this.steps.forEach((step) =>
        step.selections.forEach((selection) => {
          total += selection.variant.price * selection.qty;
          count += selection.qty;
        })
      );
      return { total, count };
    }

    encode() {
      const chunks = [];
      this.steps.forEach((step) =>
        step.selections.forEach((selection) => chunks.push(`${step.index}:${selection.variant.id}x${selection.qty}`))
      );
      return chunks.join(',');
    }

    save() {
      try {
        const build = this.encode();
        if (!build) window.localStorage.removeItem(STORAGE_KEY);
        else window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, id: this.buildId, build }));
      } catch (error) {
        /* storage can be blocked; the builder still works without it */
      }
    }

    commit() {
      this.save();
      this.refresh();
    }

    /* ----- Restore from the share link or localStorage ----- */

    async restore() {
      let entries = [];
      let fromUrl = false;

      try {
        const param = new URLSearchParams(window.location.search).get('build');
        entries = parseBuild(param);
        fromUrl = entries.length > 0;
      } catch (error) {
        entries = [];
      }

      if (!fromUrl) {
        try {
          const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
          if (saved && saved.build) {
            entries = parseBuild(saved.build);
            if (saved.id) this.buildId = String(saved.id);
          }
        } catch (error) {
          entries = [];
        }
      }

      if (!entries.length) {
        this.openFirstEmpty(false);
        return;
      }

      this.restoring = true;
      this.setMessage(this.t('restoring'), 'info');
      this.refresh();

      let missing = 0;
      const soldOut = [];
      const wanted = new Map();
      entries.forEach((entry) => {
        const step = this.stepByIndex(entry.step);
        if (!step) {
          missing += 1;
          return;
        }
        if (!wanted.has(step)) wanted.set(step, []);
        wanted.get(step).push(entry);
      });

      await Promise.all(
        Array.from(wanted.entries()).map(async ([step, list]) => {
          await this.loadStep(step);
          if (!step.products) {
            missing += list.length;
            return;
          }
          list.forEach((entry) => {
            let found = null;
            step.products.some((product) => {
              const variant = product.variants.find((item) => item.id === entry.variantId);
              if (variant) found = { product, variant };
              return Boolean(variant);
            });
            if (!found) {
              missing += 1;
              return;
            }
            if (!found.variant.available) {
              soldOut.push(found.product.title);
              return;
            }
            if (this.selectionFor(step, found.product.id)) return;
            if (!step.multiple && step.selections.length) return;
            step.selections.push({
              product: found.product,
              variant: found.variant,
              qty: step.multiple ? entry.qty : 1,
            });
          });
        })
      );

      if (!this.isConnected) return;
      this.restoring = false;

      // Drop ?build= once it has been read, so later reloads use the saved (possibly edited) build.
      if (fromUrl) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('build');
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          /* leave the address alone */
        }
      }

      const notes = [];
      if (this.totals().count) notes.push(this.t(fromUrl ? 'restoredShared' : 'restored'));
      if (missing) notes.push(this.t('partsMissing', { count: missing }));
      if (soldOut.length) notes.push(this.t('partsSoldOut', { parts: soldOut.join(', ') }));
      if (missing || soldOut.length) this.setMessage(notes.join(' '), 'warning');
      else this.setPassingMessage(notes.join(' '), 'info');

      this.commit();
      this.openFirstEmpty(false);
    }

    /* ----- Loading products ----- */

    loadStep(step) {
      if (step.products) return Promise.resolve();
      if (step.loading) return step.loading;

      step.error = false;
      this.renderList(step);

      const base = `${rootUrl()}collections/${encodeURIComponent(step.handle)}/products.json?limit=${PAGE_SIZE}`;
      const run = async () => {
        const products = [];
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const response = await fetch(`${base}&page=${page}`, {
            signal: this.abort.signal,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          const batch = Array.isArray(data.products) ? data.products : [];
          batch.forEach((raw) => products.push(normaliseProduct(raw)));
          if (batch.length < PAGE_SIZE) break;
        }
        step.products = products;
      };

      step.loading = run()
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          step.error = true;
        })
        .then(() => {
          step.loading = null;
          if (this.isConnected) {
            this.renderHeader(step);
            this.renderList(step);
          }
        });
      return step.loading;
    }

    /* ----- Events ----- */

    onClick(event) {
      const target = event.target.closest('[data-pcb-action]');
      if (!target || !this.contains(target) || target.tagName === 'SELECT') return;
      const action = target.dataset.pcbAction;
      const stepNode = target.closest('[data-pcb-step]');
      const row = target.closest('[data-product-id]');
      const step = row
        ? this.stepByIndex(row.dataset.stepIndex)
        : stepNode
        ? this.stepByIndex(stepNode.dataset.index)
        : target.dataset.stepIndex
        ? this.stepByIndex(target.dataset.stepIndex)
        : null;
      const product =
        row && step && step.products
          ? step.products.find((item) => String(item.id) === row.dataset.productId)
          : null;

      switch (action) {
        case 'toggle':
          this.setOpen(step, !step.open);
          break;
        case 'select':
          if (step && product) this.select(step, product);
          break;
        case 'remove':
          if (step && row) this.remove(step, row.dataset.productId);
          break;
        case 'inc':
        case 'dec':
          if (step && row) this.changeQuantity(step, row.dataset.productId, action === 'inc' ? 1 : -1);
          break;
        case 'more':
          step.shown += RENDER_CHUNK;
          this.renderList(step);
          break;
        case 'retry':
          this.loadStep(step);
          break;
        case 'next':
          this.advance(step);
          break;
        case 'jump':
          if (step) this.goToStep(step);
          break;
        case 'summary':
          this.scrollTo(this.summaryBox);
          this.summaryBox.focus({ preventScroll: true });
          break;
        case 'add':
          this.addToCart(target);
          break;
        case 'clear':
          this.clear();
          break;
        case 'share':
          this.share();
          break;
        default:
      }
    }

    onChange(event) {
      const target = event.target;
      const stepNode = target.closest('[data-pcb-step]');
      const step = stepNode ? this.stepByIndex(stepNode.dataset.index) : null;
      if (!step) return;

      if (target.matches('[data-pcb-sort]')) {
        step.sort = target.value;
        step.shown = RENDER_CHUNK;
        this.renderList(step);
      } else if (target.matches('[data-pcb-incompat]')) {
        step.showIncompatible = target.checked;
        this.renderList(step);
      } else if (target.matches('[data-pcb-action="variant"]')) {
        const row = target.closest('[data-product-id]');
        const product = step.products.find((item) => String(item.id) === row.dataset.productId);
        const variant = product && product.variants.find((item) => String(item.id) === target.value);
        if (!variant) return;
        step.variantChoice[product.id] = variant.id;
        const selection = this.selectionFor(step, product.id);
        if (selection && variant.available) selection.variant = variant;
        this.commit();
      }
    }

    onInput(event) {
      const target = event.target;
      if (!target.matches('[data-pcb-search]')) return;
      const step = this.stepByIndex(target.closest('[data-pcb-step]').dataset.index);
      step.query = target.value;
      step.shown = RENDER_CHUNK;
      this.renderList(step);
    }

    /* ----- Actions ----- */

    currentVariant(step, product) {
      const selection = this.selectionFor(step, product.id);
      if (selection) return selection.variant;
      const chosen = product.variants.find((variant) => variant.id === step.variantChoice[product.id]);
      return chosen || product.variants.find((variant) => variant.available) || product.variants[0];
    }

    select(step, product) {
      const variant = this.currentVariant(step, product);
      if (!variant || !variant.available) return;
      const compat = this.compatFor(step, product);
      if (compat && compat.status === 'incompatible') return;

      const existing = this.selectionFor(step, product.id);
      if (existing) existing.variant = variant;
      else if (step.multiple) step.selections.push({ product, variant, qty: 1 });
      else step.selections = [{ product, variant, qty: 1 }];

      step.el.classList.remove('is-missing');
      this.setMessage('');
      this.commit();
      this.announceTotal();
      if (!step.multiple) this.advance(step);
    }

    remove(step, productId) {
      step.selections = step.selections.filter((selection) => String(selection.product.id) !== String(productId));
      this.setMessage('');
      this.commit();
      this.announceTotal();
    }

    changeQuantity(step, productId, delta) {
      const selection = this.selectionFor(step, productId);
      if (!selection) return;
      const next = selection.qty + delta;
      if (next < 1) {
        this.remove(step, productId);
        return;
      }
      selection.qty = Math.min(MAX_QUANTITY, next);
      this.commit();
      this.announceTotal();
    }

    clear() {
      this.steps.forEach((step) => {
        step.selections = [];
        step.el.classList.remove('is-missing');
      });
      this.buildId = newBuildId();
      this.commit();
      this.setMessage(this.t('cleared'), 'info');
      this.announceTotal();
    }

    shareUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('build');
      url.hash = '';
      const base = url.toString();
      const build = this.encode();
      if (!build) return base;
      // Kept readable on purpose: digits, ":", "x" and "," are all safe in a query string.
      return `${base}${base.includes('?') ? '&' : '?'}build=${build}`;
    }

    async share() {
      if (!this.totals().count) {
        this.setMessage(this.t('emptyBuild'), 'error');
        return;
      }
      const link = this.shareUrl();
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(link);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }
      if (!copied) {
        try {
          const area = el('textarea', { class: 'visually-hidden', readonly: true, 'aria-hidden': 'true' });
          area.value = link;
          this.append(area);
          area.select();
          copied = document.execCommand('copy');
          area.remove();
        } catch (error) {
          copied = false;
        }
      }
      if (copied) {
        this.setMessage(this.t('copied'), 'success');
      } else {
        this.setMessage(this.t('copyFailed'), 'info');
        if (this.shareField) {
          this.shareField.value = link;
          this.shareField.hidden = false;
          this.shareField.focus();
          this.shareField.select();
        }
      }
    }

    async addToCart(trigger) {
      if (this.busy || this.restoring) return;

      const items = [];
      this.steps.forEach((step) =>
        step.selections.forEach((selection) =>
          items.push({ id: selection.variant.id, quantity: selection.qty, properties: { _Build: this.buildId } })
        )
      );
      const missing = this.missingRequired();
      this.steps.forEach((step) => step.el.classList.toggle('is-missing', missing.includes(step)));

      if (missing.length) {
        this.setMessage(this.t('missingRequired', { steps: missing.map((step) => step.label).join(', ') }), 'error');
        this.goToStep(missing[0]);
        return;
      }
      if (!items.length) {
        this.setMessage(this.t('emptyBuild'), 'error');
        return;
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const canRender = Boolean(
        cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function'
      );
      const body = { items };
      if (canRender) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      this.setBusy(true);
      this.setMessage('');

      try {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          const detail = data.description || data.message || this.t('cartError');
          this.setMessage(typeof detail === 'string' ? detail : this.t('cartError'), 'error');
          return;
        }

        this.buildId = newBuildId();
        this.save();
        this.setMessage(this.t('added'), 'success');
        this.refreshCart(cart, canRender, data, trigger);
      } catch (error) {
        this.setMessage(this.t('cartError'), 'error');
      } finally {
        this.setBusy(false);
      }
    }

    /** Re-renders Dawn's cart drawer or cart notification (shared helper in assets/etec.js), or goes to the cart page. */
    refreshCart(cart, canRender, data, trigger) {
      const cartUrl = (window.routes && window.routes.cart_url) || `${rootUrl()}cart`;
      const rendered = canRender && window.EtecCart && window.EtecCart.render(data, trigger);
      if (!rendered) window.location.href = cartUrl;
    }

    setBusy(state) {
      this.busy = state;
      this.querySelectorAll('[data-pcb-action="add"]').forEach((button) => {
        button.classList.toggle('is-loading', state);
      });
      this.syncAddState();
    }

    missingRequired() {
      return this.steps.filter((step) => step.required && !step.selections.length);
    }

    /**
     * The Add buttons look disabled until the build can be added. They stay clickable (aria-disabled,
     * not disabled) so a click still explains what is missing and opens that step.
     */
    syncAddState() {
      const notReady = this.busy || !this.totals().count || this.missingRequired().length > 0;
      this.querySelectorAll('[data-pcb-action="add"]').forEach((button) => {
        if (notReady) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    /* ----- Steps: open, close, move on ----- */

    setOpen(step, open) {
      if (!step) return;
      step.open = open;
      step.panel.hidden = !open;
      step.el.classList.toggle('is-open', open);
      step.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) return;
      if (step.products) this.renderList(step);
      else this.loadStep(step);
    }

    openFirstEmpty(focus) {
      const step = this.steps.find((item) => !item.selections.length);
      if (!step) return;
      this.setOpen(step, true);
      if (focus) step.toggle.focus({ preventScroll: true });
    }

    /** Closes a step and opens the next one that still needs a part. */
    advance(step) {
      const position = this.steps.indexOf(step);
      const next =
        this.steps.slice(position + 1).find((item) => !item.selections.length) ||
        this.steps.find((item) => item !== step && item.required && !item.selections.length);

      this.setOpen(step, false);
      if (!next) {
        step.toggle.focus({ preventScroll: true });
        this.scrollTo(step.el);
        return;
      }
      this.setOpen(next, true);
      next.toggle.focus({ preventScroll: true });
      this.scrollTo(next.el);
    }

    goToStep(step) {
      this.setOpen(step, true);
      step.toggle.focus({ preventScroll: true });
      this.scrollTo(step.el);
    }

    scrollTo(node) {
      if (!node || inEditor()) return;
      const rect = node.getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight * 0.5) return;
      node.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    /* ----- Messages ----- */

    setMessage(text, type) {
      window.clearTimeout(this.messageTimer);
      this.messageTimer = null;
      [this.message, this.barMessage].forEach((node) => {
        if (!node) return;
        node.textContent = text || '';
        node.hidden = !text;
        node.dataset.type = type || 'info';
      });
      if (this.shareField) this.shareField.hidden = true;
    }

    /** A notice that only confirms something: it clears after a few seconds or on the first tap or key press. */
    setPassingMessage(text, type) {
      this.setMessage(text, type);
      if (!text) return;
      const clear = () => {
        this.removeEventListener('pointerdown', clear);
        this.removeEventListener('keydown', clear);
        // messageTimer is reset by setMessage, so a newer message is never wiped.
        if (this.messageTimer) this.setMessage('');
      };
      this.messageTimer = window.setTimeout(clear, PASSING_MESSAGE_MS);
      this.addEventListener('pointerdown', clear);
      this.addEventListener('keydown', clear);
    }

    announceTotal() {
      if (!this.announcer) return;
      const { total, count } = this.totals();
      this.announcer.textContent = this.t('announce', { count, total: this.money(total) });
    }

    /* ----- Rendering ----- */

    refresh() {
      const focus = this.captureFocus();
      this.steps.forEach((step) => {
        this.renderHeader(step);
        if (step.open && step.products) this.renderList(step);
      });
      this.renderSummary();
      this.restoreFocus(focus);
    }

    captureFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active) || !active.dataset.pcbAction) return null;
      const row = active.closest('[data-product-id]');
      if (!row) return null;
      return {
        summary: Boolean(active.closest('[data-pcb-summary]')),
        step: row.dataset.stepIndex,
        product: row.dataset.productId,
        action: active.dataset.pcbAction,
      };
    }

    restoreFocus(focus) {
      if (!focus) return;
      const active = document.activeElement;
      if (active && active !== document.body && active.isConnected && this.contains(active)) return;
      const scope = focus.summary ? this.summary : this;
      const row = Array.from(scope.querySelectorAll('[data-product-id]')).find(
        (node) =>
          node.dataset.productId === focus.product &&
          node.dataset.stepIndex === focus.step &&
          Boolean(node.closest('[data-pcb-summary]')) === focus.summary
      );
      let target = null;
      if (row) {
        target =
          row.querySelector(`[data-pcb-action="${focus.action}"]:not(:disabled)`) ||
          row.querySelector('[data-pcb-primary]:not(:disabled)') ||
          row.querySelector('button:not(:disabled), select, a');
      }
      if (!target) {
        const step = this.stepByIndex(focus.step);
        target = focus.summary
          ? this.summary.querySelector(`[data-pcb-action="jump"][data-step-index="${focus.step}"]`)
          : step && step.toggle;
      }
      if (target) target.focus({ preventScroll: true });
    }

    renderHeader(step) {
      const count = step.selections.length;
      step.el.classList.toggle('is-filled', count > 0);
      if (!count) {
        step.choice.textContent = this.t(step.required ? 'required' : 'optional');
        step.price.textContent = '';
        return;
      }
      const units = step.selections.reduce((sum, selection) => sum + selection.qty, 0);
      step.choice.textContent =
        count === 1 && units === 1 ? step.selections[0].product.title : this.t('partsSelected', { count: units });
      step.price.textContent = this.money(
        step.selections.reduce((sum, selection) => sum + selection.variant.price * selection.qty, 0)
      );
    }

    renderList(step) {
      const list = step.list;
      const state = (text, extra) => {
        list.replaceChildren(el('li', { class: 'etec-pcb__state' }, el('span', { text }), extra));
        step.count.textContent = '';
        step.more.hidden = true;
        step.incompatWrap.hidden = true;
      };

      if (step.error) {
        state(
          this.t('loadError'),
          el('button', { type: 'button', class: 'etec-pcb__link', 'data-pcb-action': 'retry', text: this.t('retry') })
        );
        return;
      }
      if (!step.products) {
        list.setAttribute('aria-busy', 'true');
        state(this.t('loading'));
        return;
      }
      list.removeAttribute('aria-busy');
      if (!step.products.length) {
        state(this.t('empty'));
        return;
      }

      const words = step.query.trim().toLowerCase().split(/\s+/).filter(Boolean);
      let items = step.products
        .filter((product) => words.every((word) => product.search.includes(word)))
        .map((product) => ({
          product,
          compat: this.compatFor(step, product),
          selection: this.selectionFor(step, product.id),
        }));

      const blocked = items.filter((item) => item.compat && item.compat.status === 'incompatible' && !item.selection);
      step.incompatWrap.hidden = blocked.length === 0;
      step.incompatLabel.textContent = this.t('showIncompatible', { count: blocked.length });
      // "Showing x of y": y counts the incompatible parts too, so it agrees with "Show incompatible (n)".
      const matching = items.length;
      if (!step.showIncompatible) items = items.filter((item) => !blocked.includes(item));

      if (step.sort === 'price-asc') items.sort((a, b) => a.product.sortPrice - b.product.sortPrice);
      else if (step.sort === 'price-desc') items.sort((a, b) => b.product.sortPrice - a.product.sortPrice);
      else if (step.sort === 'name') items.sort((a, b) => a.product.title.localeCompare(b.product.title));

      if (!items.length) {
        list.replaceChildren(el('li', { class: 'etec-pcb__state' }, el('span', { text: this.t('noResults') })));
        step.count.textContent = '';
        step.more.hidden = true;
        return;
      }

      const visible = items.slice(0, step.shown);
      list.replaceChildren(...visible.map((item) => this.renderRow(step, item)));
      step.count.textContent = this.t('showing', { shown: visible.length, count: matching });
      step.more.hidden = visible.length >= items.length;
    }

    renderRow(step, item) {
      const { product, compat, selection } = item;
      const variant = this.currentVariant(step, product);
      const blocked = Boolean(compat && compat.status === 'incompatible');
      const available = Boolean(variant && variant.available);
      const image = sizedImage((variant && variant.image) || product.image, 240);

      const info = el(
        'div',
        { class: 'etec-pcb__info' },
        product.vendor ? el('span', { class: 'etec-pcb__vendor', text: product.vendor }) : null,
        el(
          'a',
          { class: 'etec-pcb__title', href: product.url, target: '_blank', rel: 'noopener' },
          product.title,
          el('span', { class: 'visually-hidden', text: ` ${this.t('newTab')}` })
        ),
        el(
          'div',
          { class: 'etec-pcb__meta' },
          el(
            'span',
            { class: `etec-stock${available ? '' : ' etec-stock--out'}` },
            el('span', { class: 'etec-stock__dot', 'aria-hidden': 'true' }),
            this.t(available ? 'inStock' : 'soldOut')
          ),
          variant && variant.sku
            ? el(
                'span',
                { class: 'etec-pcb__sku' },
                el('strong', { class: 'etec-pcb__sku-label', text: `${this.t('sku')}:` }),
                ` ${variant.sku}`
              )
            : null,
          this.renderBadge(compat)
        )
      );

      if (product.variants.length > 1) {
        const select = el(
          'select',
          {
            class: 'etec-pcb__variant',
            'data-pcb-action': 'variant',
            'aria-label': this.t('variantLabel', { part: product.title }),
          },
          product.variants.map((option) =>
            el('option', {
              value: option.id,
              disabled: !option.available,
              selected: variant && option.id === variant.id,
              text: `${option.title} - ${option.available ? this.money(option.price) : this.t('soldOut')}`,
            })
          )
        );
        info.append(select);
      }

      const price = el(
        'div',
        { class: 'etec-pcb__price' },
        variant && variant.compareAt && variant.compareAt > variant.price
          ? el('s', { class: 'etec-pcb__was', text: this.money(variant.compareAt) })
          : null,
        el('span', { class: 'etec-pcb__now', text: variant ? this.money(variant.price) : '' })
      );

      let action;
      if (selection) {
        action = el('button', {
          type: 'button',
          class: 'etec-button etec-button--ink etec-button--small etec-pcb__pick',
          'data-pcb-action': 'remove',
          'data-pcb-primary': true,
          'aria-label': this.t('removePart', { part: product.title }),
          text: this.t('remove'),
        });
      } else {
        action = el('button', {
          type: 'button',
          class: 'etec-button etec-button--accent etec-button--small etec-pcb__pick',
          'data-pcb-action': 'select',
          'data-pcb-primary': true,
          disabled: !available || blocked,
          'aria-label': `${this.t(available ? 'select' : 'soldOut')}: ${product.title}`,
          text: this.t(available ? 'select' : 'soldOut'),
        });
      }

      const classes = ['etec-pcb__item'];
      if (selection) classes.push('is-selected');
      if (blocked || !available) classes.push('is-dim');

      return el(
        'li',
        { class: classes.join(' '), data: { stepIndex: String(step.index), productId: String(product.id) } },
        el(
          'div',
          { class: 'etec-pcb__thumb' },
          // Never leave the cell empty: Dawn hides empty divs, which would collapse the row's grid.
          image
            ? el('img', { src: image, alt: '', loading: 'lazy', width: '96', height: '96' })
            : el('span', { class: 'etec-pcb__noimg', 'aria-hidden': 'true' })
        ),
        info,
        el(
          'div',
          { class: 'etec-pcb__buy' },
          price,
          selection && step.multiple ? this.renderStepper(selection) : null,
          action
        )
      );
    }

    renderBadge(compat) {
      if (!compat || compat.status === 'none') return null;
      if (compat.status === 'incompatible') {
        const names = Array.from(new Set(compat.conflicts.map((conflict) => conflict.title))).join(', ');
        return el('span', {
          class: 'etec-pcb__badge etec-pcb__badge--bad',
          text: this.t('incompatible', { part: names }),
        });
      }
      if (compat.status === 'warning') {
        return el('span', {
          class: 'etec-pcb__badge etec-pcb__badge--warn',
          text: this.t('lowHeadroom', { recommended: compat.power.recommended, draw: compat.power.draw }),
        });
      }
      if (compat.status === 'compatible') {
        return el('span', { class: 'etec-pcb__badge etec-pcb__badge--ok', text: this.t('compatible') });
      }
      return el('span', { class: 'etec-pcb__badge', text: this.t('checkCompatibility') });
    }

    /* Icons are rendered by Liquid into <template data-etec-icon> nodes; the text glyph is the fallback. */
    icon(name, fallback) {
      const template = this.querySelector(`template[data-etec-icon="${name}"]`);
      return template ? template.content.cloneNode(true) : document.createTextNode(fallback);
    }

    renderStepper(selection) {
      const part = selection.product.title;
      return el(
        'div',
        { class: 'etec-pcb__qty', role: 'group', 'aria-label': this.t('quantity') },
        el('button', {
          type: 'button',
          'data-pcb-action': 'dec',
          'aria-label': this.t('decrease', { part }),
        }, this.icon('minus', '−')),
        el('span', { class: 'etec-pcb__qty-value', text: String(selection.qty) }),
        el('button', {
          type: 'button',
          'data-pcb-action': 'inc',
          'aria-label': this.t('increase', { part }),
          disabled: selection.qty >= MAX_QUANTITY,
        }, this.icon('plus', '+'))
      );
    }

    renderSummary() {
      const { total, count } = this.totals();

      this.summary.replaceChildren(
        ...this.steps.map((step) => {
          const head = el('button', {
            type: 'button',
            class: 'etec-pcb__sum-label',
            'data-pcb-action': 'jump',
            'data-step-index': String(step.index),
            text: step.label,
          });

          if (!step.selections.length) {
            return el(
              'li',
              { class: `etec-pcb__sum-step is-empty${step.required ? ' is-required' : ''}` },
              head,
              el('span', {
                class: 'etec-pcb__sum-none',
                text: `${this.t('notSelected')}${step.required ? ` · ${this.t('required')}` : ''}`,
              })
            );
          }

          return el(
            'li',
            { class: 'etec-pcb__sum-step' },
            head,
            step.selections.map((selection) => {
              const variantTitle =
                selection.product.variants.length > 1 && selection.variant.title !== 'Default Title'
                  ? ` (${selection.variant.title})`
                  : '';
              return el(
                'div',
                {
                  class: 'etec-pcb__sum-line',
                  data: { stepIndex: String(step.index), productId: String(selection.product.id) },
                },
                el('span', { class: 'etec-pcb__sum-title', text: `${selection.product.title}${variantTitle}` }),
                el(
                  'div',
                  { class: 'etec-pcb__sum-side' },
                  step.multiple
                    ? this.renderStepper(selection)
                    : null,
                  el('span', {
                    class: 'etec-pcb__sum-price',
                    text: this.money(selection.variant.price * selection.qty),
                  }),
                  el('button', {
                    type: 'button',
                    class: 'etec-pcb__sum-remove',
                    'data-pcb-action': 'remove',
                    'aria-label': this.t('removePart', { part: selection.product.title }),
                  }, this.icon('close', '×'))
                )
              );
            })
          );
        })
      );

      this.querySelectorAll('[data-pcb-total]').forEach((node) => {
        node.textContent = this.money(total);
      });
      this.querySelectorAll('[data-pcb-parts]').forEach((node) => {
        node.textContent = String(count);
      });

      this.renderCompatibility(count);
      this.syncAddState();
      this.renderQuote(total);
    }

    renderCompatibility(count) {
      if (!this.compatBox) return;
      // Until every required step has a part there is no verdict to give, only what is still needed.
      const incomplete = this.missingRequired().length > 0;
      if (this.restoring || (!incomplete && (!this.compatibility || !count))) {
        this.compatBox.hidden = true;
        this.compatBox.replaceChildren();
        return;
      }
      const checked = this.compatibility && count > 0;
      const result = checked ? evaluateBuild(this.parts()) : { issues: [], warnings: [], unknown: false };
      const keyNames = { socket: this.t('keySocket'), ram: this.t('keyRam'), form: this.t('keyForm') };
      const lines = [];

      result.issues.forEach((issue) =>
        lines.push(
          el('li', {
            class: 'is-bad',
            text: this.t('compatIssue', { a: issue.a, b: issue.b, key: keyNames[issue.key] || issue.key }),
          })
        )
      );
      result.warnings.forEach((warning) =>
        lines.push(el('li', { class: 'is-warn', text: this.t('compatPower', warning) }))
      );
      if (incomplete) lines.push(el('li', { class: 'is-note', text: this.t('requiredFirst') }));
      else if (!lines.length && !result.unknown) lines.push(el('li', { class: 'is-ok', text: this.t('compatOk') }));
      if (result.unknown) lines.push(el('li', { class: 'is-note', text: this.t('compatUnknown') }));

      this.compatBox.replaceChildren(el('ul', { class: 'etec-pcb__compat-list', role: 'list' }, lines));
      this.compatBox.hidden = false;
    }

    renderQuote(total) {
      if (!this.quote) return;
      const base = this.dataset.quoteUrl || this.quote.getAttribute('href');
      const lines = [];
      this.steps.forEach((step) =>
        step.selections.forEach((selection) => {
          const variantTitle =
            selection.product.variants.length > 1 && selection.variant.title !== 'Default Title'
              ? ` (${selection.variant.title})`
              : '';
          lines.push(
            `${step.label}: ${selection.qty} x ${selection.product.title}${variantTitle} - ${this.money(
              selection.variant.price * selection.qty
            )}`
          );
        })
      );
      if (!lines.length) {
        this.quote.setAttribute('href', base);
        return;
      }
      lines.push(`${this.t('total')}: ${this.money(total)}`);
      const hashAt = base.indexOf('#');
      const path = hashAt < 0 ? base : base.slice(0, hashAt);
      const hash = hashAt < 0 ? '' : base.slice(hashAt);
      this.quote.setAttribute(
        'href',
        `${path}${path.includes('?') ? '&' : '?'}build=${encodeURIComponent(lines.join('\n'))}${hash}`
      );
    }
  }

  // Exposed for testing the compatibility rules from the console.
  EtecPcBuilder.compatibility = { parseTags, checkProduct, evaluateBuild };
  window.EtecPcBuilder = EtecPcBuilder;

  customElements.define('etec-pc-builder', EtecPcBuilder);
})();
